/**
 * Cookie consent storage + tiny pub/sub.
 *
 * Shape stored in localStorage under `spCookieConsent:v1`:
 *   { necessary: true, analytics: bool, marketing: bool, ts: number, version: 1 }
 *
 * Other code can call `getCookieConsent()` / `hasCookieConsent()` and
 * subscribe via `onCookieConsentChange(cb)`.
 */

export const COOKIE_CONSENT_KEY = "spCookieConsent:v1";
export const COOKIE_CONSENT_VERSION = 1;

const EVENT_NAME = "sp:cookieconsentchange";

function safeStorage() {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getCookieConsent() {
  const s = safeStorage();
  if (!s) return null;
  try {
    const raw = s.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== COOKIE_CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasCookieConsent() {
  return !!getCookieConsent();
}

export function setCookieConsent(partial) {
  const s = safeStorage();
  const value = {
    necessary: true,
    analytics: !!partial?.analytics,
    marketing: !!partial?.marketing,
    ts: Date.now(),
    version: COOKIE_CONSENT_VERSION,
  };
  if (s) {
    try {
      s.setItem(COOKIE_CONSENT_KEY, JSON.stringify(value));
    } catch {
      /* quota / private mode — ignore */
    }
  }
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value }));
    } catch {
      /* ignore */
    }
  }
  return value;
}

export function clearCookieConsent() {
  const s = safeStorage();
  if (s) {
    try { s.removeItem(COOKIE_CONSENT_KEY); } catch { /* ignore */ }
  }
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }));
    } catch {
      /* ignore */
    }
  }
}

export function onCookieConsentChange(cb) {
  if (typeof window === "undefined" || typeof cb !== "function") return () => {};
  const handler = (ev) => cb(ev.detail);
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

/**
 * Allows any UI (e.g. a "Cookie-Einstellungen" link in the footer) to
 * reopen the banner by dispatching a global event. `CookieConsent` listens
 * to it.
 */
export const OPEN_COOKIE_SETTINGS_EVENT = "sp:cookieconsentopen";
export function openCookieSettings() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
  } catch {
    /* ignore */
  }
}
